import { useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { normalizePhone } from "../lib/phone";
import { register, type Sex } from "../lib/api";
import PhoneInput from "../components/PhoneInput";
import { useT } from "../i18n";
import { errorMessage } from "../i18n/errors";

interface Dob {
  d: number;
  m: number;
  y: number;
}

// Точный возраст на сегодня (с учётом месяца и дня).
function ageOf({ d, m, y }: Dob): number {
  const today = new Date();
  let age = today.getFullYear() - y;
  if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) age--;
  return age;
}

// Календарная валидность (отсекает 31.02 и т.п.).
function isRealDate({ d, m, y }: Dob): boolean {
  if (y < 1920 || y > new Date().getFullYear()) return false;
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

// Открытая регистрация (без SMS-кода) — решение маркетинга. Отправляем телефон,
// ник и пол; сервер сам заводит участника и выдаёт сессию.
// День рождения — только фронтовый гейт «с 16 лет», на бэк не отправляется.
const DOB_LOCALE: Record<string, string> = { kk: "kk-KZ", ru: "ru-RU", en: "en-US" };

// Маска "+7 (XXX) XXX-XX-XX" из нормализованного номера (8XXXXXXXXXX) — для
// предзаполнения PhoneInput, когда телефон приходит из экрана входа.
function maskPhone(normalized: string): string {
  const d = normalized.replace(/\D/g, "").replace(/^[78]/, "").slice(0, 10);
  if (!d) return "";
  return `+7 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8, 10)}`;
}

export default function Register() {
  const nav = useNavigate();
  const { t, lang } = useT();
  const [params] = useSearchParams();
  // С экрана входа приходим с телефоном (аккаунта не оказалось) — предзаполняем.
  const fromLogin = params.get("from") === "login";
  const [phone, setPhone] = useState(() => maskPhone(params.get("phone") || ""));
  const [nick, setNick] = useState("");
  const [sex, setSex] = useState<Sex | "">("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // День рождения: подтверждённая дата + состояние модалки ввода ДД-ММ-ГГГГ.
  const [dob, setDob] = useState<Dob | null>(null);
  const [dobOpen, setDobOpen] = useState(false);
  const [dd, setDd] = useState("");
  const [mm, setMm] = useState("");
  const [yyyy, setYyyy] = useState("");
  const [dobError, setDobError] = useState("");
  const mmRef = useRef<HTMLSelectElement | null>(null);
  const yyyyRef = useRef<HTMLInputElement | null>(null);

  // Названия месяцев на языке интерфейса (для скролл-выбора).
  const monthNames = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        new Intl.DateTimeFormat(DOB_LOCALE[lang] ?? "ru-RU", { month: "long" }).format(new Date(2000, i, 1)),
      ),
    [lang],
  );

  function openDob() {
    setDd(dob ? String(dob.d).padStart(2, "0") : "");
    setMm(dob ? String(dob.m) : "");
    setYyyy(dob ? String(dob.y) : "");
    setDobError("");
    setDobOpen(true);
  }

  function confirmDob() {
    setDobError("");
    const candidate: Dob = { d: Number(dd), m: Number(mm), y: Number(yyyy) };
    if (dd.length < 1 || !mm || yyyy.length !== 4 || !isRealDate(candidate))
      return setDobError(t("register.errBadDate"));
    if (ageOf(candidate) < 16) return setDobError(t("register.errUnderage"));
    setDob(candidate);
    setDobOpen(false);
  }

  const fmt2 = (n: number) => String(n).padStart(2, "0");

  async function submit() {
    setError("");
    const p = normalizePhone(phone);
    if (!p) return setError(t("register.errInvalidPhone"));
    if (!nick.trim()) return setError(t("register.errNoNick"));
    if (!/^[A-Za-z0-9_]{2,20}$/.test(nick.trim())) return setError(t("register.errNickLatin"));
    // Гейт «с 16 лет» — только на фронте, дата на сервер не уходит.
    if (!dob) return setError(t("register.errNoDob"));
    if (ageOf(dob) < 16) return setError(t("register.errUnderage"));
    if (sex !== "M" && sex !== "F") return setError(t("register.errNoSex"));
    setLoading(true);
    try {
      await register(p, nick.trim(), sex as Sex);
      nav(`/hub?phone=${encodeURIComponent(p)}`);
    } catch (e: any) {
      setError(errorMessage(e, t));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-dvh flex flex-col px-7 py-8 max-w-md mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-extrabold tracking-tight">{t("register.title")}</h1>
        <p className="text-muted text-sm mt-1">{t("register.subtitle")}</p>
      </header>

      {fromLogin && (
        <div className="mb-4 rounded-[14px] border border-gold/40 bg-gold/[0.08] px-4 py-3 text-[13px] leading-[1.5] text-gold-light font-semibold">
          {t("register.fromLogin")}
        </div>
      )}

      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-2">
          <span className="text-sm text-white/80">{t("register.phoneLabel")}</span>
          <PhoneInput value={phone} onChange={setPhone} />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-sm text-white/80">{t("register.nickLabel")}</span>
          <input
            value={nick}
            // Ник — только латиница/цифры/underscore, без пробелов: он публично
            // светится в лидерборде, и так проще избежать дублей и мусора.
            onChange={(e) => setNick(e.target.value.replace(/[^A-Za-z0-9_]/g, "").slice(0, 20))}
            placeholder={t("register.nickPlaceholder")}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className="w-full bg-input text-black rounded-[14px] px-4 py-3 text-base placeholder:text-grey/70 outline-none focus:ring-2 focus:ring-violet"
          />
        </label>
        <div className="flex flex-col gap-2">
          <span className="text-sm text-white/80">{t("register.dobLabel")}</span>
          <button
            type="button"
            onClick={openDob}
            className={`w-full bg-input rounded-[14px] px-4 py-3 text-base text-left outline-none focus:ring-2 focus:ring-violet ${
              dob ? "text-black" : "text-grey/70"
            }`}
          >
            {dob ? `${fmt2(dob.d)}.${fmt2(dob.m)}.${dob.y}` : t("register.dobPlaceholder")}
          </button>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-sm text-white/80">{t("register.sexLabel")}</span>
          <div className="grid grid-cols-2 gap-3">
            {(["F", "M"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSex(s)}
                className={`h-12 rounded-[14px] text-base font-semibold transition-colors ${
                  sex === s
                    ? "bg-violet-grad text-white shadow-violet"
                    : "bg-white/[0.06] text-white active:bg-white/10"
                }`}
              >
                {s === "F" ? t("register.sexF") : t("register.sexM")}
              </button>
            ))}
          </div>
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
      </div>

      <div className="flex flex-col gap-3 mt-6">
        <button
          type="button"
          onClick={submit}
          disabled={loading}
          className="w-full h-14 rounded-[14px] bg-violet-grad text-white font-extrabold text-base active:opacity-90 transition-opacity disabled:opacity-60 shadow-violet-btn"
        >
          {loading ? t("register.submitting") : t("register.submit")}
        </button>
        <button
          type="button"
          onClick={() => nav("/login")}
          className="w-full h-12 rounded-[14px] bg-white/[0.06] text-white font-medium text-sm ring-1 ring-white/10 active:bg-white/10 transition-colors"
        >
          {t("register.haveAccount")}
        </button>
      </div>

      {/* Модалка ввода дня рождения: ДД — ММ — ГГГГ, гейт «с 16 лет» */}
      {dobOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 px-4 py-6"
          onClick={() => setDobOpen(false)}
        >
          <div
            className="w-full max-w-md bg-card border border-line-3 rounded-[22px] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-extrabold tracking-tight">{t("register.dobLabel")}</h2>
            <p className="text-muted text-sm mt-1">{t("register.dob.note")}</p>

            <div className="flex items-center gap-2.5 mt-5">
              <input
                value={dd}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 2);
                  setDd(v);
                  if (v.length === 2) mmRef.current?.focus();
                }}
                placeholder={t("register.dob.day")}
                inputMode="numeric"
                autoFocus
                className="flex-1 min-w-0 bg-input text-black rounded-[14px] px-2 py-3 text-lg text-center placeholder:text-grey/60 outline-none focus:ring-2 focus:ring-violet"
              />
              <span className="text-muted2 font-bold">—</span>
              <select
                ref={mmRef}
                value={mm}
                onChange={(e) => {
                  setMm(e.target.value);
                  yyyyRef.current?.focus();
                }}
                className={`flex-[1.6] min-w-0 bg-input rounded-[14px] px-2 py-3 text-lg text-center outline-none focus:ring-2 focus:ring-violet appearance-none ${
                  mm ? "text-black" : "text-grey/60"
                }`}
              >
                <option value="" disabled>
                  {t("register.dob.month")}
                </option>
                {monthNames.map((name, i) => (
                  <option key={i} value={String(i + 1)}>
                    {name}
                  </option>
                ))}
              </select>
              <span className="text-muted2 font-bold">—</span>
              <input
                ref={yyyyRef}
                value={yyyy}
                onChange={(e) => setYyyy(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder={t("register.dob.year")}
                inputMode="numeric"
                className="flex-[1.5] min-w-0 bg-input text-black rounded-[14px] px-2 py-3 text-lg text-center placeholder:text-grey/60 outline-none focus:ring-2 focus:ring-violet"
              />
            </div>

            {dobError && <p className="text-sm text-red-400 mt-3">{dobError}</p>}

            <button
              type="button"
              onClick={confirmDob}
              className="w-full h-13 mt-5 py-3.5 rounded-[14px] bg-violet-grad text-white font-extrabold text-base active:opacity-90 transition-opacity shadow-violet-btn"
            >
              {t("register.dob.confirm")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
